import { Button, DateInput, JoonUIColor, Typography } from "@joonapp/web-shared"
import dayjs from "dayjs"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useScreenTimeRestrictionsConfirmationModalStore } from "./ScreenTimeRestrictionsConfirmationModal"
import { CloseButton } from "../../../components/closeButton/CloseButton"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import TimeInputNew from "../../../components/timeInput/TimeInputNew"
import {
  useCreateScreenTimeManualExceptionMutation,
  useDeleteScreenTimeManualExceptionMutation,
} from "../../../networking/rewards/mutations"
import { ScreenTimeRestrictionsConfirmationModalType } from "../../../types/rewards"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { convertTo12HourTime } from "../../../util/util"
import useRewardsUserSelectStore from "../hooks/useRewardsUserSelectStore"

interface ToggleScreenTimeRestrictionsModalStore {
  isOpen: boolean
  onOpen: (currentException: string | null) => void
  onClose: () => void
  restrictionEndDate: Date
  setRestrictionEndDate: (date: Date) => void
  restrictionEndTime: string
  setRestrictionEndTime: (time: string) => void
  isTogglingOn: boolean
}

export const useToggleScreenTimeRestrictionsModalStore =
  create<ToggleScreenTimeRestrictionsModalStore>((set) => ({
    isOpen: false,
    onOpen: (currentException: string | null) =>
      set({ isOpen: true, isTogglingOn: !!currentException }),
    onClose: () =>
      set({
        isOpen: false,
        restrictionEndDate: new Date(),
        restrictionEndTime: dayjs().add(1, "hour").format("HH:mm:ss"),
      }),
    restrictionEndDate: new Date(),
    setRestrictionEndDate: (date: Date) => set({ restrictionEndDate: date }),
    restrictionEndTime: dayjs().add(1, "hour").format("HH:mm:ss"),
    setRestrictionEndTime: (time: string) => set({ restrictionEndTime: time }),
    isTogglingOn: false,
  }))

const ToggleScreenTimeRestrictionsModal = () => {
  const { selectedUserId } = useRewardsUserSelectStore()
  const {
    isOpen,
    onClose,
    restrictionEndDate,
    setRestrictionEndDate,
    restrictionEndTime,
    setRestrictionEndTime,
    isTogglingOn,
  } = useToggleScreenTimeRestrictionsModalStore()
  const { onOpen: onOpenScreenTimeRestrictionsConfirmationModal } =
    useScreenTimeRestrictionsConfirmationModalStore()
  const createScreenTimeManualExceptionMutation =
    useCreateScreenTimeManualExceptionMutation()
  const deleteScreenTimeManualExceptionMutation =
    useDeleteScreenTimeManualExceptionMutation()
  const { t } = useTranslation()

  const formattedTime = convertTo12HourTime(restrictionEndTime)
  const date = dayjs(restrictionEndDate)
  const dateString = date.isSame(dayjs(), "day")
    ? ""
    : `${date.format("MMM D")} ${t("TSTR_AT")} `

  const onTurnOffRestriction = async () => {
    if (
      date.isSame(dayjs(), "day") &&
      dayjs().format("HH:mm") > restrictionEndTime
    ) {
      toast.error(t("TSTR_ERR_FUTURE"))
      return
    }
    if (!selectedUserId) return
    const dateTimeISOString = dayjs(
      `${dayjs(restrictionEndDate).format("YYYY-MM-DD")}T${restrictionEndTime}`
    ).toISOString()
    await createScreenTimeManualExceptionMutation.mutateAsync({
      userId: selectedUserId,
      end_datetime: dateTimeISOString,
    })
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_CREATE_MANUAL_EXCEPTION)
    onClose()
    onOpenScreenTimeRestrictionsConfirmationModal(
      ScreenTimeRestrictionsConfirmationModalType.TURN_OFF
    )
  }

  const onTurnOnRestriction = async () => {
    if (!selectedUserId) return
    await deleteScreenTimeManualExceptionMutation.mutateAsync(selectedUserId)
    onClose()
    onOpenScreenTimeRestrictionsConfirmationModal(
      ScreenTimeRestrictionsConfirmationModalType.TURN_ON
    )
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      backgroundType="overlay"
      size="fit-content"
    >
      <div className="w-full h-full flex flex-col align-start gap-6 p-4 bg-bgXLightGray">
        <CloseButton onClick={onClose} />
        <div className="flex flex-col align-start gap-4">
          <Typography variant="bodyBold" className="w-[95%]">
            {isTogglingOn ? t("TSTR_TURN_BACK_ON") : t("TSTR_TURN_OFF")}
          </Typography>
          <Typography variant="bodySmall" className="whitespace-pre-line">
            {isTogglingOn ? t("TSTR_BACK_ON_DESC") : t("TSTR_TURN_OFF_DESC")}
          </Typography>
          {!isTogglingOn && (
            <div className="flex flex-row items-center justify-between gap-2 w-full">
              <div className="w-[calc(100%-145px)]">
                <DateInput
                  name="restrictionEndDate"
                  date={restrictionEndDate}
                  modalTitle={t("TSTR_SELECT_DATE")}
                  setDate={setRestrictionEndDate}
                  minDate={new Date()}
                  fullWidth
                />
              </div>
              <TimeInputNew
                value={restrictionEndTime}
                setValue={setRestrictionEndTime}
              />
            </div>
          )}
        </div>
        {isTogglingOn ? (
          <div className="flex flex-row items-center gap-2 w-full">
            <Button
              buttonType="secondary"
              onClick={onClose}
              text={t("QB_CANCEL")}
              fullWidth
              disabled={deleteScreenTimeManualExceptionMutation.isLoading}
            />
            <Button
              buttonType="secondary"
              onClick={onTurnOnRestriction}
              text={
                <Typography
                  variant="body"
                  className="whitespace-wrap"
                  color={JoonUIColor.text.primaryAccent}
                >
                  {t("TSTR_TURN_ON_RESTRICTIONS")}
                </Typography>
              }
              fullWidth
              isLoading={deleteScreenTimeManualExceptionMutation.isLoading}
            />
          </div>
        ) : (
          <Button
            onClick={onTurnOffRestriction}
            text={
              <Typography
                variant="body"
                className="whitespace-wrap"
                color={JoonUIColor.text.inverted}
              >
                {t("TSTR_TURN_OFF_UNTIL", {
                  when: `${dateString}${formattedTime}`,
                })}
              </Typography>
            }
            fullWidth
            isLoading={createScreenTimeManualExceptionMutation.isLoading}
          />
        )}
      </div>
    </SlideUpModal>
  )
}

export default ToggleScreenTimeRestrictionsModal
